import classNames from 'classnames';
import { nanoid } from 'nanoid';
import { forwardRef, useCallback, useMemo, useRef, useState } from 'react';

import { QUESTION_TYPES_CARRY_FORWARD_SOURCE } from '@api/instant_survey';
import type { CarryForwardLogic, Question, QuestionAnswer } from '@api/instant_survey/types';
import {
  Button,
  Checkbox,
  Fieldset,
  HelpIcon,
  IconButton,
  Layout,
  Overlay,
  Pill,
  Popover,
  Radio,
  Select,
} from '@utilities';
import type { PopoverProps } from '@utilities/components/Popover';
import type { SelectOption, SelectProps } from '@utilities/components/Select';

import { useSurveyContext } from '../../../../../../../../utilities/context';

import styles from './_index.module.scss';

export interface CarryForwardProps {
  carryForwardLogic?: null | Partial<CarryForwardLogic>;
  onDeleteAnswers: (answers: QuestionAnswer[]) => void;
  onUpdateQuestion: (change: Partial<Question>) => void;
  question: Question;
  questionsBeforeCurrent: Question[];
  setCarryForwardLogic: React.Dispatch<React.SetStateAction<null | Partial<CarryForwardLogic>>>;
}

const CarryForward = ({
  carryForwardLogic,
  onDeleteAnswers,
  onUpdateQuestion,
  question,
  questionsBeforeCurrent,
  setCarryForwardLogic,
}: CarryForwardProps) => {
  const { questionDisplayIndexById } = useSurveyContext();
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const pillRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const handleApply = useCallback(
    (logicToApply: Partial<CarryForwardLogic>) => {
      const sourceSurveyQuestionId = logicToApply.sourceSurveyQuestionId;
      const selectedQuestion = questionsBeforeCurrent.find((q) => q.id === sourceSurveyQuestionId);

      if (
        sourceSurveyQuestionId !== carryForwardLogic?.sourceSurveyQuestionId &&
        selectedQuestion
      ) {
        const carryForwardAnswers = selectedQuestion.answers.filter(
          ({ isNoneOfTheAbove }) => !isNoneOfTheAbove
        );
        const newDeletedAnswers = question.answers.filter(
          ({ isCarryForwardGenerated }) => isCarryForwardGenerated
        );
        const newAnswers = [
          ...carryForwardAnswers.map(({ isAnchored, isExclusive, ...answer }) => ({
            ...answer,
            id: nanoid(),
            isCarryForwardGenerated: true,
          })),
          ...question.answers.filter((answer) => !answer.isCarryForwardGenerated),
        ];

        onDeleteAnswers(newDeletedAnswers);
        onUpdateQuestion({ answers: newAnswers });
      }

      setCarryForwardLogic(logicToApply);
      setIsPopoverOpen(false);
    },
    [
      carryForwardLogic,
      onDeleteAnswers,
      onUpdateQuestion,
      question.answers,
      questionsBeforeCurrent,
      setCarryForwardLogic,
    ]
  );

  const handleToggleChecked = (currentValue: boolean) => {
    const nextValue = !currentValue;
    if (!nextValue) {
      const deletedAnswers = question.answers.filter(
        ({ isCarryForwardGenerated }) => isCarryForwardGenerated
      );
      const remainingAnswers = question.answers.filter(
        ({ isCarryForwardGenerated }) => !isCarryForwardGenerated
      );

      onDeleteAnswers(deletedAnswers);
      onUpdateQuestion({
        answers: remainingAnswers,
      });
      setCarryForwardLogic(null);
      setIsPopoverOpen(false);
    } else {
      setCarryForwardLogic({
        mode: 'SELECTED',
        sourceSurveyQuestionId: undefined,
      });
    }
  };

  const isChecked = Boolean(carryForwardLogic);
  const selectedQuestion = questionsBeforeCurrent.find(
    (q) => q.id === carryForwardLogic?.sourceSurveyQuestionId
  );
  const pillValue = selectedQuestion
    ? `Q${questionDisplayIndexById[selectedQuestion.id]}. ${selectedQuestion.text}`
    : 'Select Question';

  return (
    <Layout.Flex className={styles['carry-forward']} gap="x-small">
      <Checkbox
        className={styles['carry-forward-checkbox']}
        isChecked={isChecked}
        label="Carry forward previous answers"
        onChange={handleToggleChecked}
        value={isChecked}
      />

      {isChecked && (
        <div className={styles['carry-forward-pill-container']} ref={popoverRef}>
          <Pill
            buttonRef={pillRef}
            className={styles['carry-forward-pill']}
            data-testid="carry-forward-pill"
            isActive={isPopoverOpen}
            isRequired={!selectedQuestion}
            onClick={() => setIsPopoverOpen(!isPopoverOpen)}
            value={pillValue}
          />
          <Overlay
            container={popoverRef.current}
            onHide={() => setIsPopoverOpen(false)}
            placement="bottom-start"
            rootClose
            show={isPopoverOpen}
            target={pillRef.current}
          >
            <CarryForwardPopover
              carryForwardLogic={carryForwardLogic}
              onApply={handleApply}
              questionsBeforeCurrent={questionsBeforeCurrent}
            />
          </Overlay>
        </div>
      )}

      {/* @ts-ignore - IconButton is still jsx */}
      <IconButton
        as="a"
        className={styles['carry-forward-help-button']}
        href="https://help.numerator.com/en/articles/10099418-in-platform-surveys-variables"
        rel="noopener noreferrer"
        target="_blank"
        tooltip="Help Center"
      >
        <HelpIcon />
      </IconButton>
    </Layout.Flex>
  );
};

interface CarryForwardPopoverProps extends PopoverProps {
  carryForwardLogic?: null | Partial<CarryForwardLogic>;
  onApply: (logicToApply: Partial<CarryForwardLogic>) => void;
  questionsBeforeCurrent: Question[];
}

const CarryForwardPopover = forwardRef<HTMLDivElement, CarryForwardPopoverProps>(
  (
    {
      carryForwardLogic,
      className,
      onApply,
      questionsBeforeCurrent,
      ...props
    }: CarryForwardPopoverProps,
    ref
  ) => {
    const { questionDisplayIndexById } = useSurveyContext();
    const [logicToApply, setLogicToApply] = useState({
      ...(carryForwardLogic ?? {}),
      mode: carryForwardLogic?.mode || 'SELECTED',
    });
    const questionOptions = useMemo<SelectOption[]>(
      () =>
        questionsBeforeCurrent
          .filter(({ type }) => QUESTION_TYPES_CARRY_FORWARD_SOURCE.includes(type))
          .map(({ id, text }) => ({
            label: `Q${questionDisplayIndexById[id]}. ${text}`,
            value: id,
          })),
      [questionDisplayIndexById, questionsBeforeCurrent]
    );

    const handleSelectQuestion = (option: SelectOption) => {
      updateLogic({ sourceSurveyQuestionId: option.value as number });
    };
    const updateLogic = (changes: Partial<CarryForwardLogic>) => {
      setLogicToApply((prev) => ({
        ...prev,
        ...changes,
      }));
    };

    const selectedOption = questionOptions.find(
      (option) => option.value === logicToApply.sourceSurveyQuestionId
    );

    return (
      <Popover
        className={classNames(className, styles['carry-forward-popover'])}
        data-testid="carry-forward-popover"
        ref={ref}
        {...props}
      >
        <Popover.Body>
          <Layout.Flex.Column gap="large">
            <Select
              className={styles['carry-forward-popover-select']}
              label="Question"
              onChange={handleSelectQuestion as SelectProps['onChange']}
              options={questionOptions}
              placeholder="Select question"
              value={selectedOption}
            />
            <div className={styles['carry-forward-popover-divider']} />
            <Fieldset legend="Additional Options">
              <div className={styles['carry-forward-popover-options']}>
                <Radio
                  isChecked={logicToApply.mode === 'SELECTED'}
                  label="Selected by panelist"
                  onChange={() => updateLogic({ mode: 'SELECTED' })}
                  value="SELECTED"
                />
                <Radio
                  isChecked={logicToApply.mode === 'UNSELECTED'}
                  label="Not selected by panelist"
                  onChange={() => updateLogic({ mode: 'UNSELECTED' })}
                  value="UNSELECTED"
                />
              </div>
            </Fieldset>
            <div className={styles['carry-forward-popover-footer']}>
              <Button
                isDisabled={!selectedOption}
                onClick={() => onApply(logicToApply)}
                text="Apply"
              />
            </div>
          </Layout.Flex.Column>
        </Popover.Body>
      </Popover>
    );
  }
);

CarryForwardPopover.displayName = 'CarryForwardPopover';

export default CarryForward;
